import {
  isPlatformBrowser,
  Location,
  LocationStrategy,
  PathLocationStrategy,
} from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_ID,
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  NgModule,
  PLATFORM_ID,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import {
  FullRouterStateSerializer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OtpVerificationModalComponent } from '@shared/components/otp-verification-modal/otp-verification-modal.component';
import { HttpRequestModule } from '@shared/modules/http-request/http-request.module';
import { ValidatorService } from '@shared/services';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import { environment } from '@src/environments';
import { effects } from '@store/effects';
import { CustomSerializer, reducers } from '@store/reducers';
import { metaReducers } from '@store/reducers/storage/storage-meta.reducer';
import { AccordionModule } from '@vg-constellation/angular-16/accordion';
import { ButtonModule } from '@vg-constellation/angular-16/button';
import { LinkModule } from '@vg-constellation/angular-16/link';
import { ModalDialogModule } from '@vg-constellation/angular-16/modal-dialog';
import { CookieService } from 'ngx-cookie-service';
import {
  CddModule,
  MfeLoadModule,
  VgimEmploymentModule,
  VgimPersonalAddressModule,
  VgimPersonalInfoClientModule,
  VgimPersonalPhoneModule,
  VgimRecaptchaModule,
} from 'vg-interaction-model';

import { AppGlobals } from './app.globals';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './core/containers/main/app.component';
import { FeatureToggleInitializerService } from './core/services/feature-toggle-initializer.service';
import { QueryHandlerService } from './core/services/query-handler-service';
import { UnmaskAttributeService } from './core/services/unmask-attribute.service';
import { OtelModule } from './otel.module';
import { ContactInfoModule } from './secure/contact-info/contact-info.module';
import { OpenChoiceModule } from './secure/open-choice/open-choice.module';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    OtelModule,
    MfeLoadModule,
    AccordionModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({
      appId: 'change-of-ownership-angular',
    }),
    ButtonModule,
    CddModule.forRoot({
      // TODO: Figure this out
      // serviceDomain: environment.midTierDomain,
      environment: environment.vgimEnv,
      application: 'COO',
    }),
    EffectsModule.forRoot(effects),
    FormsModule,
    HttpRequestModule,
    ModalDialogModule.forRoot(),
    LinkModule,
    ReactiveFormsModule,
    ContactInfoModule,
    VgimEmploymentModule.forRoot({ application: 'COO', environment: environment.vgimEnv }),
    VgimPersonalAddressModule.forRoot({ application: 'COO', environment: environment.vgimEnv }),
    VgimPersonalInfoClientModule.forRoot({ application: 'COO', environment: environment.vgimEnv }),
    VgimPersonalPhoneModule.forRoot({ application: 'COO', environment: environment.vgimEnv }),
    VgimRecaptchaModule.forRoot({ application: 'COO', environment: environment.vgimEnv }),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    StoreDevtoolsModule.instrument({
      autoPause: true,
      trace: true,
      traceLimit: 75,
      logOnly: environment.isProduction,
      connectInZone: true,
    }),
    OpenChoiceModule,
    OtpVerificationModalComponent,
  ],
  providers: [
    AppGlobals,
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    ValidatorService,
    Location,
    QueryHandlerService,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    CookieService,
    WindowRef,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: (featureToggleInitializer: FeatureToggleInitializerService) =>
        featureToggleInitializer.initialize(),
      deps: [FeatureToggleInitializerService],
      multi: true,
    },
  ],
})
export class AppModule {
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(APP_ID) private appId: string,
    private queryHandlerService: QueryHandlerService,
    private unmaskAttributeService: UnmaskAttributeService,
  ) {
    const platform = isPlatformBrowser(platformId) ? 'in the browser' : 'on the server';
    console.log(`Running ${platform} with appId=${appId}`);

    if (isPlatformBrowser(platformId)) {
      this.unmaskAttributeService.initialize();
    }
  }
}
